import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { COLORS, TYPOGRAPHY } from '../../../styles/UISystem';
import SendButton from '../Buttons/SendButton';
import { Phone } from '@phosphor-icons/react/dist/ssr/Phone';
import { InstagramLogo } from '@phosphor-icons/react/dist/ssr/InstagramLogo';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { PhoneInputField } from '../Forms/PhoneInputField';
import { createAutofillDetector } from '../../../utils/autofillDetection';

/**
 * ChatInputMorphing
 *
 * The Anon's single morphing input (exchange contract #3/#4): the same field in
 * the composer slot walks name → contact, then the parent hands back to the
 * standard ChatInputNormal path once a contact classifies.
 *
 * PLACEHOLDERS ARE THE GUIDE (exchange-clarity D3, 2026-07-26 — supersedes the
 * 2026-07-17 open-exchange "no placeholder" ruling): a state-derived
 * placeholder progression carries the guidance the old guide beats wanted —
 * "What's your name?" → "Contact or social" — reborn inside the input, zero
 * chrome. Field evidence for the reversal: connections literally saved as
 * "Hello" and "Ayoooo" (the empty composer asked the wrong implicit question).
 * Only the morph stages get placeholders; the post-contact standard composer
 * stays plain. Name extraction v1 stays behind it (placeholder steers,
 * extractor catches) — the name stage still takes typed text verbatim;
 * own-bubble tap is the recovery.
 *
 * Stages (parent-derived from alreadySharedName/alreadySharedTypes):
 * - 'name': empty field, the name autofill chip is the guide; paper-plane send
 *   once text is entered; name autofill never auto-submits.
 * - 'contact': ONE freetext field with exactly THREE states (Decided Design
 *   #7). Three REAL single-purpose inputs, only the ACTIVE one MOUNTED (device
 *   pass 3: coexisting email+tel inputs flip iOS into multi-field
 *   "AutoFill Contact" mode). The swap mounts + focuses inside the tap gesture
 *   (flushSync) — spike-proven; the keyboard never drops.
 *     anything (default) — normal keyboard + the email autofill HINT on a
 *                          plain text field (device pass 4); icons [phone][insta]
 *     phone              — number pad + phone QuickType; icons [insta][+]
 *     insta              — email KEY LAYOUT via inputMode; "@" written into the
 *                          field as REAL text (the strongest wordless hint — it
 *                          rides into the chat bubble); icons [phone][+]
 *   "+" returns to the anything-state. Anything typed classifies by content
 *   (contactClassify) or lands as chat. Icons are direct-tap 44px targets;
 *   real content swaps them for the paper-plane (a lone "@" is not content).
 *   iOS autofill on this stage auto-submits (createAutofillDetector).
 *
 * Geometry matches ChatInputNormal (§2.2) so the post-contact handoff to the
 * standard composer is seamless.
 *
 * @param {'name'|'contact'} stage
 * @param {function} onNameSubmit - (name: string)
 * @param {function} onContactSubmit - ({ raw, fieldState }) — the parent owns
 *   classification and delivery; raw is exactly what was typed (or E.164 from
 *   the phone state)
 */

const MIN_HEIGHT = 50;

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: `${MIN_HEIGHT}px`,
  backgroundColor: 'white',
  border: `1px solid ${COLORS.MEDIUM_DARK_GRAY}`,
  borderRadius: '25px',
  padding: '5px 5px 5px 16px',
  boxSizing: 'border-box',
  position: 'relative',
};

const bareInputStyle = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  padding: '10px 0',
  ...TYPOGRAPHY.BODY_REGULAR,
  color: COLORS.DARK_GRAY,
};

// Direct-tap type-switch icons: full-size targets (44px), real spacing —
// per-icon taps earned back from the checkpoint-A one-target rule by size
// (refine Q7; mis-tap fallback is one target opening a picker).
const iconTapStyle = {
  width: '44px',
  height: '44px',
  border: 'none',
  backgroundColor: 'transparent',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  flexShrink: 0,
};

// Each state's icons are simply the other two states (Decided Design #7)
const STATE_ICONS = {
  anything: [
    { target: 'phone', Icon: Phone, label: 'Share a phone number' },
    { target: 'insta', Icon: InstagramLogo, label: 'Share Instagram' },
  ],
  phone: [
    { target: 'insta', Icon: InstagramLogo, label: 'Share Instagram' },
    { target: 'anything', Icon: Plus, label: 'Share anything else' },
  ],
  insta: [
    { target: 'phone', Icon: Phone, label: 'Share a phone number' },
    { target: 'anything', Icon: Plus, label: 'Share anything else' },
  ],
};

// WebKit paints autofilled inputs yellow; the only reliable override is
// painting an inset shadow over it (user ruling from the spike device check).
const autofillUnhighlight = (scope) => `
  ${scope}:-webkit-autofill,
  ${scope}:-webkit-autofill:hover,
  ${scope}:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset;
    -webkit-text-fill-color: ${COLORS.DARK_GRAY};
    transition: background-color 99999s ease-out;
  }
`;

const ChatInputMorphing = ({ stage, onNameSubmit, onContactSubmit }) => {
  // --- name stage ---
  const [nameValue, setNameValue] = useState('');

  // --- contact stage: three-state field ---
  const [fieldState, setFieldState] = useState('anything');
  const [contactValue, setContactValue] = useState('');
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);
  const autofillDetectorRef = useRef(null);
  const anythingRef = useRef(null);
  const instaRef = useRef(null);
  const phoneRef = useRef(null); // PhoneInputField imperative handle ({ focus, blur })

  // Fresh autofill detector each time the contact stage begins — a prior
  // stage's timestamps must not feed detection
  useEffect(() => {
    if (stage === 'contact') {
      autofillDetectorRef.current = createAutofillDetector(Date.now());
    }
  }, [stage]);

  // Re-mint the anything-input shortly after the contact stage appears (before
  // anyone taps it). Seven device passes: an input born inside the
  // name->contact React commit NONDETERMINISTICALLY inherits a name-first
  // AutoFill classification; an input inserted fresh into a settled page
  // classified correctly in every observed test (spike P10 + the user's
  // cycle-and-return observation). The remount is invisible — the field is
  // unfocused this early — and skipped if it somehow has focus.
  const [freshKey, setFreshKey] = useState(0);
  useEffect(() => {
    if (stage !== 'contact') return undefined;
    const t = setTimeout(() => {
      if (document.activeElement !== anythingRef.current) {
        setFreshKey((k) => k + 1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [stage]);

  const trimmedContact = contactValue.trim();
  // A lone "@" is the Instagram state's pre-filled hint, not content
  const hasRealContent = trimmedContact !== '' && trimmedContact !== '@';

  const submitName = () => {
    const trimmed = nameValue.trim();
    if (!trimmed) return;
    onNameSubmit(trimmed);
    setNameValue('');
  };

  const submitContact = () => {
    if (!hasRealContent) return;
    onContactSubmit({ raw: trimmedContact, fieldState });
    setContactValue('');
  };

  // Field-swap: the visibility change must complete INSIDE this tap's event so
  // focus() lands on a visible input within the user gesture — that is what
  // keeps the keyboard up and engages the new field's QuickType (device-proven
  // on the autofill spike; an async render + focus loses the bar).
  const switchFieldState = (target) => {
    if (target === fieldState) return;
    flushSync(() => {
      setFieldState(target);
      // "@" is written as REAL text in the Instagram state — the strongest
      // wordless hint, and it rides into the chat bubble (open-exchange ruling)
      setContactValue(target === 'insta' ? '@' : '');
    });
    const ref = target === 'phone' ? phoneRef : target === 'insta' ? instaRef : anythingRef;
    ref.current?.focus();
    autofillDetectorRef.current = createAutofillDetector(Date.now());
  };

  // Contact-field change: record the keystroke gap; a detected autofill burst
  // arms the pending flag (submit happens post-settle in the effect below)
  const handleContactChange = (value) => {
    setContactValue(value);
    const detector = autofillDetectorRef.current;
    if (detector) {
      detector.track();
      if (detector.wasAutofilled()) setAutoSubmitPending(true);
    }
  };

  // Auto-submit after autofill: wait for the value to settle (100ms with no
  // further changes — submitting inside the onChange tick truncates the value),
  // then submit through the normal path.
  useEffect(() => {
    if (!autoSubmitPending) return;
    const t = setTimeout(() => {
      setAutoSubmitPending(false);
      autofillDetectorRef.current?.reset();
      if (hasRealContent) submitContact();
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitPending, contactValue]);

  const handleEnterSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitContact();
    }
  };

  if (stage === 'name') {
    return (
      <div style={containerStyle}>
        <style>{autofillUnhighlight('.chat-morph-name')}</style>
        <input
          className="chat-morph-name"
          type="text"
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitName();
            }
          }}
          autoComplete="name"
          // "sentences", not "words" (exchange-polish item 11): the autocomplete
          // is the ideal path, but a typed sentence ("Hi it's Dimitri") must not
          // fight the keyboard capitalizing Every Word
          autoCapitalize="sentences"
          // D3: the contraction, per the recorded mock delta ("What is your
          // name?" in the frame; contraction confirmed at /go:blocks)
          placeholder="What's your name?"
          style={bareInputStyle}
        />
        {nameValue.trim() ? <SendButton onClick={submitName} /> : null}
      </div>
    );
  }

  // --- contact stage ---
  return (
    <div className="chat-morph-contact-wrap" style={containerStyle}>
      <style>{autofillUnhighlight('.chat-morph-contact-wrap input')}</style>

      {/* anything-state: free chat with the email autofill HINT kept
          deliberately (user ruling 2026-07-21: a rest-state affordance that
          costs nothing — one keystroke dismisses it, and the whole field
          retires on the first classified contact). Normal-chat capitalization
          (sentences — exchange-polish item 14); autocorrect stays OFF because
          this field doubles as the bare-handle entry path. Only the ACTIVE
          input is MOUNTED (device pass 3). */}
      {fieldState === 'anything' && (
        <input
          key={`fresh-${freshKey}`}
          ref={anythingRef}
          type="text"
          autoComplete="email"
          autoCapitalize="sentences"
          autoCorrect="off"
          value={contactValue}
          onChange={(e) => handleContactChange(e.target.value)}
          onKeyDown={handleEnterSubmit}
          // D3: anything-state only — the phone/insta typed states keep their
          // own mechanics (empty tel field; pre-written "@")
          placeholder="Contact or social"
          style={bareInputStyle}
        />
      )}

      {/* phone state: PhoneInputField (E.164 out, tel QuickType). height 48px =
          the other fields' input box — PhoneInputField has no internal vertical
          padding, so without this the phone state renders 10px shorter */}
      {fieldState === 'phone' && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
            height: '48px',
            display: 'flex',
          }}
        >
          <PhoneInputField
            ref={phoneRef}
            value={contactValue}
            onChange={handleContactChange}
            placeholder=""
            autoComplete="tel"
            name="rf2"
            embedded={true}
            hideDialCode={!phoneFocused}
            onFocus={() => setPhoneFocused(true)}
            onBlur={() => setPhoneFocused(false)}
            onKeyDown={handleEnterSubmit}
          />
        </div>
      )}

      {/* insta state: email KEY LAYOUT (inputMode) on a non-email field, "@"
          pre-written as real text — no email QuickType, trivial detection */}
      {fieldState === 'insta' && (
        <input
          ref={instaRef}
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoCapitalize="none"
          value={contactValue}
          onChange={(e) => handleContactChange(e.target.value)}
          onKeyDown={handleEnterSubmit}
          style={bareInputStyle}
        />
      )}

      {/* Right edge says one thing at a time: what else you could share while
          there's no real content; send once there is */}
      {hasRealContent ? (
        <SendButton onClick={submitContact} />
      ) : (
        STATE_ICONS[fieldState].map(({ target, Icon, label }) => (
          <button
            key={target}
            aria-label={label}
            onClick={() => switchFieldState(target)}
            style={iconTapStyle}
          >
            <Icon size={24} color="#8E8E93" weight="light" />
          </button>
        ))
      )}
    </div>
  );
};

ChatInputMorphing.propTypes = {
  stage: PropTypes.oneOf(['name', 'contact']).isRequired,
  onNameSubmit: PropTypes.func.isRequired,
  onContactSubmit: PropTypes.func.isRequired,
};

export default ChatInputMorphing;
